/**
 * APEX v7.7.0 — Daily Mission
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md section 5.
 */

import { DAY_MS, WEEK_MS, MONTH_MS } from '../constants';

export const MISSION_TYPES = {
  GREED_CONTROL: 'greed_control',
  CHAMPION_FOCUS: 'champion_focus',
  LANE_FOCUS: 'lane_focus',
  SESSION_DISCIPLINE: 'session_discipline',
  COOLDOWN: 'cooldown',
  MAINTAIN: 'maintain',
  WELCOME: 'welcome'
};

// ============================================================
// Helpers
// ============================================================

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function filterByDate(games, sinceMs) {
  if (!Array.isArray(games)) return [];
  const cutoff = Date.now() - sinceMs;
  return games.filter(g => {
    const ts = g.timestamp;
    if (!ts) return false;
    return new Date(ts).getTime() > cutoff;
  });
}

function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

function countGreedDeaths(game) {
  if (!game?.deathDetails) return 0;
  return game.deathDetails.filter(d => d?.type === 'greed').length;
}

function avgGreedPerGame(games) {
  const valid = games.filter(g => !isCancelled(g));
  if (valid.length === 0) return 0;
  return valid.reduce((sum, g) => sum + countGreedDeaths(g), 0) / valid.length;
}

function isLaneWon(game) {
  const me = game?.timeFrames?.min15?.my || {};
  const opp = game?.timeFrames?.min15?.opp || {};
  const checks = [
    (me.gold || 0) - (opp.gold || 0) >= 300,
    (me.cs || 0) - (opp.cs || 0) >= 5,
    (me.xp || 0) - (opp.xp || 0) >= 300
  ];
  return checks.filter(Boolean).length >= 2;
}

function laneWinRateAt15(games) {
  const valid = games.filter(g => !isCancelled(g) && g.timeFrames?.min15);
  if (valid.length === 0) return null;
  const wins = valid.filter(isLaneWon).length;
  return Math.round((wins / valid.length) * 100);
}

function gamesYesterday(games) {
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = startOfToday.getTime() - DAY_MS;
  return games.filter(g => {
    const ts = g.timestamp;
    if (!ts) return false;
    const t = new Date(ts).getTime();
    return t >= startOfYesterday && t < startOfToday.getTime();
  }).length;
}

function lossStreak(games) {
  if (!Array.isArray(games) || games.length === 0) return 0;
  const sorted = [...games]
    .filter(g => !isCancelled(g))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  let streak = 0;
  for (const g of sorted) {
    if (g.result === 'L') streak++;
    else break;
  }
  return streak;
}

function findStrugglingMain(games, pool) {
  if (!Array.isArray(pool) || pool.length === 0) return null;
  const mains = pool.filter(p => p?.status === 'main' && p.name);
  if (mains.length === 0) return null;
  let worst = null;
  for (const main of mains) {
    const champGames = games.filter(g => !isCancelled(g) && g.champion === main.name);
    if (champGames.length < 5) continue;
    const wins = champGames.filter(g => g.result === 'W').length;
    const wr = Math.round((wins / champGames.length) * 100);
    if (wr < 45 && (worst === null || wr < worst.wr)) {
      worst = { name: main.name, games: champGames.length, wr };
    }
  }
  return worst;
}

// ============================================================
// API publique
// ============================================================

export function generateDailyMission({ games, championPool }) {
  const safeGames = Array.isArray(games) ? games : [];
  const pool = Array.isArray(championPool) ? championPool : [];
  const today = todayKey();

  if (safeGames.length === 0) {
    // v7.8.8 : plus de mission "importe une game" (retirée à la demande).
    // On retourne null → pas de mission générée tant qu'il n'y a pas de data.
    return null;
  }

  const last7Days = filterByDate(safeGames, WEEK_MS);
  const last30Days = filterByDate(safeGames, MONTH_MS);

  if (last7Days.length === 0) {
    return {
      type: MISSION_TYPES.MAINTAIN,
      label: 'Reviens jouer une game',
      description: 'On reprend doucement, pas de pression',
      target: { games: 1 },
      reason: 'Pas de game depuis plus de 7 jours',
      auto_generated: true,
      created_for_date: today
    };
  }

  const greedAvg = avgGreedPerGame(last7Days);
  if (greedAvg >= 2.5) {
    return {
      type: MISSION_TYPES.GREED_CONTROL,
      label: '3 games clean',
      description: 'Max 1 greed death par game',
      target: { games: 3, maxGreedPerGame: 1 },
      reason: `Ta semaine = ${greedAvg.toFixed(1)} greed/game`,
      auto_generated: true,
      created_for_date: today
    };
  }

  const strugglingChamp = findStrugglingMain(last30Days, pool);
  if (strugglingChamp) {
    return {
      type: MISSION_TYPES.CHAMPION_FOCUS,
      label: `2 games sur ${strugglingChamp.name}`,
      description: `Tu galères sur ${strugglingChamp.name} (${strugglingChamp.wr}% WR). Force le repractice.`,
      target: { games: 2, champion: strugglingChamp.name },
      reason: `${strugglingChamp.games} games / ${strugglingChamp.wr}% WR récent`,
      auto_generated: true,
      created_for_date: today
    };
  }

  const laneWr = laneWinRateAt15(last7Days);
  if (laneWr !== null && laneWr < 45) {
    return {
      type: MISSION_TYPES.LANE_FOCUS,
      label: '2 games en lane won @15',
      description: 'Garde gold+xp+cs > opp à 15min',
      target: { games: 2, criterion: 'lane_won' },
      reason: `Ton lane WR récent = ${laneWr}%`,
      auto_generated: true,
      created_for_date: today
    };
  }

  const yesterdayCount = gamesYesterday(safeGames);
  if (yesterdayCount >= 4) {
    return {
      type: MISSION_TYPES.SESSION_DISCIPLINE,
      label: 'Max 3 ranked aujourd\'hui',
      description: `Tu en as fait ${yesterdayCount} hier. Respecte ta règle.`,
      target: { maxGamesToday: 3 },
      reason: `Hier = ${yesterdayCount} ranked games`,
      auto_generated: true,
      created_for_date: today
    };
  }

  const streak = lossStreak(safeGames);
  if (streak >= 3) {
    return {
      type: MISSION_TYPES.COOLDOWN,
      label: 'Pause + 1 game seulement',
      description: 'Après 3L, prendre 30 min puis 1 game cool',
      target: { games: 1, postBreak: true },
      reason: `${streak} défaites consécutives`,
      auto_generated: true,
      created_for_date: today
    };
  }

  return {
    type: MISSION_TYPES.MAINTAIN,
    label: '3 games solides',
    description: 'Garde ton niveau',
    target: { games: 3 },
    reason: 'Pas de pattern critique détecté',
    auto_generated: true,
    created_for_date: today
  };
}

export function getAlternativeMissions({ games, championPool }) {
  const pool = Array.isArray(championPool) ? championPool : [];
  const today = todayKey();
  const alternatives = [];

  alternatives.push({
    type: MISSION_TYPES.GREED_CONTROL,
    label: '3 games clean (max 1 greed)',
    description: 'Focus survie. Max 1 greed death par game.',
    target: { games: 3, maxGreedPerGame: 1 },
    reason: 'Suggestion alternative',
    auto_generated: false,
    created_for_date: today
  });

  const mains = pool.filter(p => p?.status === 'main' && p.name);
  if (mains.length > 0) {
    const pick = mains[Math.floor(Math.random() * mains.length)];
    alternatives.push({
      type: MISSION_TYPES.CHAMPION_FOCUS,
      label: `2 games sur ${pick.name}`,
      description: `Force le repractice sur ${pick.name}.`,
      target: { games: 2, champion: pick.name },
      reason: 'Suggestion alternative',
      auto_generated: false,
      created_for_date: today
    });
  } else {
    alternatives.push({
      type: MISSION_TYPES.LANE_FOCUS,
      label: '2 games en lane won @15',
      description: 'Garde gold+xp+cs > opp à 15min',
      target: { games: 2, criterion: 'lane_won' },
      reason: 'Suggestion alternative',
      auto_generated: false,
      created_for_date: today
    });
  }

  alternatives.push({
    type: MISSION_TYPES.MAINTAIN,
    label: '1 game à fond',
    description: 'Full focus, full attention. Qualité > quantité.',
    target: { games: 1 },
    reason: 'Suggestion alternative',
    auto_generated: false,
    created_for_date: today
  });

  return alternatives;
}

export function buildMissionRecord(mission) {
  return {
    type: 'daily_mission',
    status: 'active',
    target_games: mission.target?.games || 0,
    total_games: 0,
    // v7.7 fix : champs legacy de l'ancien schéma mini_blocks (NOT NULL hérités)
    // remplis défensivement pour rester compatible si la table ne les a pas
    // rendus nullables. Sans effet si les colonnes n'existent pas.
    title: mission.label || 'Mission du jour',
    description: mission.description || '',
    metric_type: 'custom',
    metric_operator: 'gte',
    metric_target: mission.target?.games || 0,
    data: {
      mission_type: mission.type,
      label: mission.label,
      description: mission.description,
      target: mission.target,
      reason: mission.reason,
      created_for_date: mission.created_for_date,
      auto_generated: mission.auto_generated,
      progress: { gamesContributed: 0 }
    }
  };
}

export function isMissionForToday(miniBlock) {
  if (!miniBlock?.data?.created_for_date) return false;
  return miniBlock.data.created_for_date === todayKey();
}

export function evaluateGameContribution(game, mission) {
  if (!game || !mission || isCancelled(game)) {
    return { contributes: false, reason: 'Game annulée ou invalide' };
  }
  const missionType = mission.data?.mission_type || mission.type;
  const target = mission.data?.target || mission.target || {};

  switch (missionType) {
    case MISSION_TYPES.GREED_CONTROL: {
      const greedCount = countGreedDeaths(game);
      const max = target.maxGreedPerGame ?? 1;
      return {
        contributes: greedCount <= max,
        reason: greedCount <= max ? `${greedCount} greed (≤${max}) ✓` : `${greedCount} greed (>${max}) ✗`
      };
    }
    case MISSION_TYPES.CHAMPION_FOCUS: {
      const match = game.champion === target.champion;
      return {
        contributes: match,
        reason: match ? `Joué sur ${game.champion} ✓` : `Joué sur ${game.champion} (attendu : ${target.champion})`
      };
    }
    case MISSION_TYPES.LANE_FOCUS: {
      const won = isLaneWon(game);
      return { contributes: won, reason: won ? `Lane won @15 ✓` : `Lane lost/draw @15 ✗` };
    }
    case MISSION_TYPES.SESSION_DISCIPLINE:
    case MISSION_TYPES.COOLDOWN:
    case MISSION_TYPES.MAINTAIN:
      return { contributes: true, reason: 'Game comptabilisée ✓' };
    default:
      return { contributes: false, reason: 'Type inconnu' };
  }
}

export function updateMissionProgress(mission, newGames) {
  if (!mission) return null;
  const currentProgress = mission.data?.progress?.gamesContributed || 0;
  let added = 0;
  for (const game of newGames || []) {
    const { contributes } = evaluateGameContribution(game, mission);
    if (contributes) added++;
  }
  const newCount = currentProgress + added;
  const targetGames = mission.target_games || mission.data?.target?.games || 1;

  let newStatus = mission.status || 'active';
  if (added > 0) {
    newStatus = newCount >= targetGames ? 'completed' : 'in_progress';
  }

  return {
    status: newStatus,
    data: {
      ...mission.data,
      progress: { gamesContributed: newCount }
    }
  };
}

export function countMissionProgress(mission, games) {
  if (!mission || !Array.isArray(games)) return 0;
  return games.filter(g => evaluateGameContribution(g, mission).contributes).length;
}
